import React from 'react';
import Navbar2 from '../components/NavBar2';
import DemoForm from '../components/DemoForm';
import { 
  Rocket,
  Clock,
  ArrowRight,
  Zap,
  BarChart,
  Database,
  Brain,
  Shield,
  ChartLine,
  Settings,
  CheckCircle,
  Users
} from 'lucide-react';

// Reuse your AnimatedBackground component
const AnimatedBackground = () => (
  <div className="fixed inset-0 overflow-hidden -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-blue-500/10 to-cyan-500/10 animate-pulse-slow opacity-40" />
    
    <div className="relative w-full h-full">
      <div 
        className="absolute top-[10%] left-[20%] w-[800px] h-[800px] rounded-full 
                  bg-gradient-to-r from-purple-500/20 to-blue-500/20 
                  blur-xl animate-orbit-slower mix-blend-screen opacity-40"
      />
      <div 
        className="absolute top-[30%] right-[15%] w-[600px] h-[600px] rounded-full 
                  bg-gradient-to-r from-blue-500/20 to-cyan-500/20 
                  blur-2xl animate-figure-8-slow mix-blend-screen opacity-40"
      />
    </div>

    <div 
      className="absolute inset-0 opacity-30" 
      style={{
        backgroundImage: `radial-gradient(circle at center, cyan 1px, transparent 1px), 
                        radial-gradient(circle at center, cyan 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        backgroundPosition: '0 0, 20px 20px',
        animation: 'particleShift 30s linear infinite'
      }}
    />
  </div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50 hover:border-cyan-500/50 transition-all duration-300">
    <div className="mb-4 text-cyan-400">
      <Icon className="h-8 w-8" />
    </div>
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-400">{description}</p>
  </div>
);

const StatCard = ({ value, label }) => (
  <div className="bg-gray-900/30 backdrop-blur-sm rounded-lg p-6 text-center">
    <div className="text-3xl font-bold text-white mb-2">{value}</div>
    <div className="text-gray-400">{label}</div>
  </div>
);

const MainLandingPage = () => {
  return (
    <div className="min-h-screen relative text-white">
      <AnimatedBackground />
      <Navbar2 />
      
      {/* Hero Section */}
      <div className="relative pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
            Automate Your Data Analysis Pipeline
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Say goodbye to manual work. Streamline your workflow, save time, and focus on what really matters—making data-driven decisions.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="bg-cyan-500 hover:bg-violet-500 text-white px-8 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center">
              <Rocket className="mr-2 h-5 w-5" />
              Try the Beta for Free
            </button>
            <button className="bg-gray-900/50 backdrop-blur-sm hover:bg-gray-900/70 text-white px-8 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center">
              <ArrowRight className="mr-2 h-5 w-5" />
              Request a Demo
            </button>
          </div>
        </div>
      </div>

      {/* Problem Statement Section */}
      <div id="problem-section" className="relative py-20 bg-black/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              Struggling with Time-Consuming and Inefficient Data Analysis?
            </h2>
            <p className="text-xl text-gray-400">
              We understand your challenges
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50">
              <Clock className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Hours Wasted</h3>
              <p className="text-gray-400">Repetitive tasks eating away at your productive time</p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50">
              <Settings className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Complex Processes</h3>
              <p className="text-gray-400">Multiple tools and complicated workflows slowing you down</p>
            </div>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50">
              <ChartLine className="h-8 w-8 text-cyan-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Delayed Insights</h3>
              <p className="text-gray-400">Critical decisions waiting on time-consuming analysis</p>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div id="solution-section" className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              The Only Platform That Automates the Entire Data Analysis Pipeline
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              From data ingestion to visualization, our platform does it all—faster, smarter, and effortlessly.
            </p>
          </div>

          <div id="features-section" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={Zap}
              title="End-to-End Automation"
              description="Turn raw data into insights with zero manual effort"
            />
            <FeatureCard
              icon={BarChart}
              title="Customizable Dashboards"
              description="Tailor insights to meet your unique needs"
            />
            <FeatureCard
              icon={Database}
              title="Seamless Integrations"
              description="Works with Power BI, Tableau, and Excel"
            />
            <FeatureCard
              icon={Brain}
              title="AI-Driven Insights"
              description="Predict trends and uncover patterns effortlessly"
            />
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div id="stats-section" className="relative py-20 bg-black/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StatCard value="50%" label="Faster Insights" />
            <StatCard value="60%" label="Time Saved" />
            <StatCard value="24/7" label="Support Available" />
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Data Analysis?</h2>
          <p className="text-xl text-gray-400 mb-8">
            Join the beta program today and be among the first to experience the future of data analysis.
          </p>
          <button className="bg-cyan-500 hover:bg-violet-500 text-white px-8 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center">
            <Rocket className="mr-2 h-5 w-5" />
            Join the Beta Now
          </button>
        </div>
      </div>

      {/* Demo Form Section */}
      <div id="demo-form" className="relative py-20">
        <DemoForm />
      </div>

      {/* Footer */}
      <footer className="relative bg-black/50 backdrop-blur-xl py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <p className="text-center text-gray-400">
              © 2026 MLense Platform. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLandingPage;